import React, { Component } from "react";
import moment from "moment";
import _ from "lodash";
import { connect } from "react-redux";
import { Table, FormControl } from "react-bootstrap";
import { withRouter } from "react-router";
import { userActions, typeActions } from "../../webapi";
import { usersUpdate } from "../../actions";
import {
	Button,
	OverlayPage,
	OverlayPageContents,
	OverlayPageSection,
	OverlayPageBottomButtons,
	CheckBox,
	Text,
	DropdownInput,
	SuccessPopup,
} from "../../components";

import Papa from "papaparse";
import { PHONE_COUNTRY_CODE, requiresEmail } from "../../config";
import {
	COLOUR_RED,
	TEXT_DARK,
	COLOUR_BRANDING_ACTION,
	hexToRGBAstring,
} from "../../js";

// Display names (case-insensitive) that identify the resident/primary default type.
const RESIDENT_DISPLAY_NAMES = ["resident", "residents", "primary user"];

class BulkImport extends Component {
	constructor(props) {
		super(props);
		this.state = {
			submitting: false,
			success: false,

			PageError: null,

			uploadingFile: false,
			types: [],
			csvData: [],
			sendWelcome: false,

			applyAllType: "",
			applyAllScope: "unmatched",

			fileError: false,

			successResult: null,

			profileFields: [],
			customFields: [],
		};
	}

	componentDidMount() {
		this.getUserTypes();
		this.getProfileFields();
		this.getCustomFields();
	}

	getUserTypes() {
		typeActions
			.getUserTypes(this.props.auth.site)
			.then((res) => {
				this.setState({
					types: _.sortBy(res.data, (t) => {
						return t.permissions ? t.permissions.length : 0;
					}),
				});
			})
			.catch((error) => {});
	}

	getProfileFields = async () => {
		try {
			const { data } = await userActions.getProfileFieldConfig(
				this.props.auth.site,
			);
			const profileFields = (data || []).map((field) => {
				return {
					...field,
					fieldType: "PF",
				};
			});
			this.setState({ profileFields: profileFields });
		} catch (error) {
			console.log("getProfileConfig - error", error);
		} finally {
			this.setState({ loading: false });
		}
	};

	getCustomFields = async () => {
		try {
			const { data } = await userActions.getUserFieldsConfig(
				this.props.auth.site,
			);
			const customFields = Object.keys(data || {}).map((key) => {
				return {
					...data[key],
					key,
					fieldType: "CF",
				};
			});
			this.setState({ customFields: customFields || [] });
		} catch (error) {
			console.log("getProfileConfig - error", error);
		} finally {
			this.setState({ loading: false });
		}
	};

	getAdditionalFieldDefinitions = () => {
		return [...this.state.customFields, ...this.state.profileFields];
	};

	// Human-readable name of the site this upload writes to. `auth.site` is the
	// payload key; siteInfo.siteName is the display name shown across the platform.
	currentSiteName = () => {
		const { auth } = this.props;
		const roles = auth.user && auth.user.Roles ? auth.user.Roles : [];
		const role = _.find(roles, (r) => r.site === auth.site);
		return role && role.siteInfo && !_.isEmpty(role.siteInfo.siteName)
			? role.siteInfo.siteName
			: auth.site;
	};

	// The headers the importer recognises, including this site's configured custom/profile fields.
	buildTemplateColumns = () => {
		const base = [
			"Name",
			"Phone Number",
			"Email",
			"Address",
			"Birthday",
			"User Type",
			"Tags",
			"Visible in App",
		];
		const additional = this.getAdditionalFieldDefinitions().map(
			(f) => `${f.fieldType}_${f.key}`,
		);
		return [...base, ...additional];
	};

	// Download a CSV template with the correct headers and one sample row.
	downloadTemplate = () => {
		const columns = this.buildTemplateColumns();
		const resident = this.findResidentDefault();

		const sample = {
			Name: "Jane Citizen",
			"Phone Number": "0400 000 000",
			Email: "jane@example.com",
			Address: "Unit 1",
			Birthday: "01/01/1950",
			"User Type": resident ? resident.displayName : "Primary User",
			Tags: "Example Tag",
			"Visible in App": "TRUE",
		};
		this.getAdditionalFieldDefinitions().forEach((f) => {
			sample[`${f.fieldType}_${f.key}`] = f.type === "bool" ? "TRUE" : "";
		});

		const csv = Papa.unparse({ fields: columns, data: [sample] });
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", "user-import-template.csv");
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	isMasterType = (t) => {
		if (!t) return false;
		const typeName = String(t.typeName || "").toLowerCase();
		const category = String(t.category || "").toLowerCase();
		return typeName === "master" || category === "master";
	};

	// Types offered for bulk assignment — master types are never selectable.
	selectableTypes = () => this.state.types.filter((t) => !this.isMasterType(t));

	// Resolve the default resident/primary type in priority order so a renamed
	// primary type is still found: displayName label, then exact "PrimaryUser"
	// typeName, then any resident-category type.
	findResidentDefault = () => {
		// Never let the default resolve to a master type, even on a misconfigured site.
		const types = this.state.types.filter((t) => !this.isMasterType(t));
		const byDisplayName = _.find(types, (t) =>
			RESIDENT_DISPLAY_NAMES.includes(
				String(t.displayName || "")
					.trim()
					.toLowerCase(),
			),
		);
		if (byDisplayName) return byDisplayName;
		const byTypeName = _.find(types, (t) => t.typeName === "PrimaryUser");
		if (byTypeName) return byTypeName;
		return _.find(
			types,
			(t) => String(t.category || "").toLowerCase() === "resident",
		);
	};

	// Case-insensitive match on typeName then displayName (mirrors the backend resolver).
	findTypeByValue = (value) => {
		const lower = String(value || "")
			.trim()
			.toLowerCase();
		if (!lower) return null;
		return (
			_.find(
				this.state.types,
				(t) => String(t.typeName || "").toLowerCase() === lower,
			) ||
			_.find(
				this.state.types,
				(t) => String(t.displayName || "").toLowerCase() === lower,
			) ||
			null
		);
	};

	// Derive each row's effective Type/TypeDisplay from the file match, the optional
	// "apply to all" selection, and the resident default. Master rows stay typeless.
	recomputeTypes = () => {
		const { csvData, applyAllType, applyAllScope } = this.state;
		const resident = this.findResidentDefault();
		const chosen = applyAllType ? this.findTypeByValue(applyAllType) : null;

		const updated = csvData.map((row) => {
			if (row._isMaster) {
				return { ...row, Type: "", TypeDisplay: "" };
			}
			const fileType = row._fileType
				? this.findTypeByValue(row._fileType)
				: null;
			let effective = null;
			if (chosen) {
				effective = applyAllScope === "all" ? chosen : fileType || chosen;
			} else {
				effective = fileType || resident;
			}
			return {
				...row,
				Type: effective ? effective.typeName : "",
				TypeDisplay: effective ? effective.displayName : "",
			};
		});

		this.setState({ csvData: updated });
	};

	onApplyAllTypeChange = (key) => {
		this.setState({ applyAllType: key }, this.recomputeTypes);
	};

	onApplyAllScopeChange = (key) => {
		this.setState({ applyAllScope: key }, this.recomputeTypes);
	};

	renderTypeControls() {
		const { applyAllType, applyAllScope, csvData } = this.state;
		// Only offer a bulk override when at least one row matched a type from the file.
		const anyMatched = _.some(csvData, (row) => !!row._fileType);
		if (!anyMatched) return null;

		const typeOptions = [{ Key: "", Title: "Use type from file" }].concat(
			this.selectableTypes().map((t) => ({
				Key: t.typeName,
				Title: t.displayName,
			})),
		);
		const selected = applyAllType ? this.findTypeByValue(applyAllType) : null;

		const scopeOptions = [
			{ Key: "unmatched", Title: "Only users without a matched type" },
			{ Key: "all", Title: "All users (override matched types)" },
		];
		const selectedScope = _.find(scopeOptions, (o) => o.Key === applyAllScope);

		return (
			<div className="marginTop-16 marginBottom-8" style={{ maxWidth: 300 }}>
				<Text type="formLabel">Apply a user type</Text>
				<Text type="help" className="marginBottom-8">
					Optionally assign one user type across this import. Master types are
					not available.
				</Text>
				<DropdownInput
					id="applyAllType"
					placeholder="User type"
					value={selected ? selected.displayName : "Use type from file"}
					options={typeOptions}
					onSelect={this.onApplyAllTypeChange}
				/>
				{applyAllType ? (
					<DropdownInput
						id="applyAllScope"
						placeholder="Apply to"
						value={selectedScope ? selectedScope.Title : ""}
						options={scopeOptions}
						onSelect={this.onApplyAllScopeChange}
					/>
				) : null}
			</div>
		);
	}

	handleSubmit() {
		const { csvData, fileError, submitting, sendWelcome } = this.state;

		if (_.isEmpty(csvData) || fileError) return;
		if (submitting) return;

		if (_.some(csvData, (row) => row._isMaster)) {
			this.setState({
				PageError:
					"Master user types cannot be imported. Please remove or change those rows before uploading.",
			});
			return;
		}

		// Strip internal-only fields (file-match metadata) before sending.
		const payload = csvData.map((row) =>
			_.omitBy(row, (_value, key) => key.startsWith("_")),
		);

		this.setState({ submitting: true, PageError: null });

		userActions
			.bulkNewUser(payload, this.props.auth.site, sendWelcome)
			.then((res) => {
				console.log("return response from bulk upload");
				console.log(res);
				this.setState({
					success: true,
					submitting: false,
					successResult: res,
				});
				this.props.usersUpdate(this.props.auth.site);
			})
			.catch((error) => {
				console.log("error response");
				console.log(error);
				this.setState({
					success: false,
					PageError: "Something when wrong, please try again.",
				});
			});
	}

	handleChange(event) {
		var stateChange = {};
		stateChange[event.target.getAttribute("id")] = event.target.value;
		this.setState(stateChange);
	}

	handeOnPhoneChange(value) {
		this.setState({
			userPhone: value,
		});
	}

	selectUpType(type) {
		this.setState({ uploadOption: type });
	}

	hasMasterRows = () => _.some(this.state.csvData, (row) => row._isMaster);

	canSubmit = () =>
		this.state.csvData.length > 0 &&
		!this.state.fileError &&
		!this.state.submitting &&
		!this.hasMasterRows();

	cellStyle = (invalid) => ({
		backgroundColor: invalid ? COLOUR_RED : "transparent",
		color: invalid ? "#fff" : TEXT_DARK,
	});

	renderSubmit() {
		if (this.state.success) return null;
		return (
			<div>
				<Button
					inline
					buttonType="tertiary"
					onClick={() => window.history.back()}
					isActive
					style={{ marginRight: 16 }}
				>
					Cancel
				</Button>
				{this.state.submitting ? (
					<Button inline buttonType="secondary">
						Uploading…
					</Button>
				) : (
					<Button
						inline
						buttonType="primary"
						onClick={this.handleSubmit.bind(this)}
						isActive={this.canSubmit()}
					>
						Upload users
					</Button>
				)}
			</div>
		);
	}

	renderSuccess() {
		if (!this.state.success) {
			return null;
		}
		return (
			<SuccessPopup
				text={`User list uploaded to ${this.currentSiteName()}. Accounts are being created in the background.`}
				buttons={[
					{
						type: "outlined",
						text: "View upload progress",
						onClick: () => this.props.history.push("/usershub/bulkuploads"),
					},
					{
						type: "outlined",
						text: "Go to home",
						onClick: () => window.history.back(),
					},
				]}
			/>
		);
	}

	renderError() {
		if (!this.state.PageError) return null;
		return (
			<Text type="error" className="marginTop-16">
				{this.state.PageError}
			</Text>
		);
	}

	setCSV(data) {
		const newData = [...data];

		const additionalFields = this.getAdditionalFieldDefinitions();

		_.forEach(newData, (item, index) => {
			let RowIsEmpty = true;
			Object.keys(item).forEach((key) => {
				item[key] = _.trim(item[key]);

				if (key.indexOf("CF_") === 0 || key.indexOf("PF_") === 0) {
					const field = _.find(additionalFields, (f) => {
						return key === `${f.fieldType}_${f.key}`;
					});
					if (!field) {
						delete item[key];
					} else if (field.type === "bool") {
						item[key] = item[key] === "TRUE" ? "TRUE" : "FALSE";
					} else if (_.isEmpty(item[key])) {
						delete item[key];
					}
				}

				if (_.isEmpty(key)) {
					delete item[key];
				} else if (!_.isUndefined(item[key]) && !_.isEmpty(item[key])) {
					RowIsEmpty = false;
				}
			});

			item.RowIsEmpty = RowIsEmpty;

			if (!_.isEmpty(item["Phone Number"]) && _.isEmpty(item.Phone)) {
				item.Phone = item["Phone Number"];
				delete item["Phone Number"];
			}

			if (!_.isEmpty(item["User Type"]) && _.isEmpty(item.Type)) {
				item.Type = item["User Type"];
				delete item["User Type"];
			}

			if (_.isEmpty(item.Phone)) {
				delete item.Phone;
			} else {
				if (item.Phone.startsWith("+")) {
					item.Phone = String(item.Phone).replace(/[^\d]/g, "");
					item.Phone = `+${item.Phone}`;
				} else {
					item.Phone = String(item.Phone).replace(/[^\d]/g, "");
					if (item.Phone.startsWith("0")) item.Phone = item.Phone.substring(1);
					if (item.Phone.startsWith(PHONE_COUNTRY_CODE.substring(1)))
						item.Phone = item.Phone.substring(2);
					item.Phone = `${PHONE_COUNTRY_CODE}${item.Phone}`;
				}
			}

			// Resolve the user type from the file (case-insensitive). Master types are
			// never imported — flag the row so it can be highlighted and blocked.
			// The effective Type/TypeDisplay are derived later in recomputeTypes().
			delete item._fileType;
			delete item._fileTypeDisplay;
			delete item._isMaster;
			if (!_.isEmpty(item.Type)) {
				const type = this.findTypeByValue(item.Type);
				if (type && this.isMasterType(type)) {
					item._isMaster = true;
				} else if (type) {
					item._fileType = type.typeName;
					item._fileTypeDisplay = type.displayName;
				}
			}
			delete item.Type;
			delete item.TypeDisplay;

			if (_.isEmpty(item.Email)) {
				delete item.Email;
			} else {
				item.Email = _.trim(item.Email.toLowerCase());
			}

			item.Name = _.trim(item.Name);

			if (_.isEmpty(item.Unit)) {
				if (!_.isEmpty(item.Address)) {
					item.Unit = _.trim(item.Address);
				} else {
					delete item.Unit;
				}
			}

			if (_.isEmpty(item.Birthday)) {
				delete item.Birthday;
			} else {
				let birthday = moment(
					item.Birthday,
					_.includes(item.Birthday, "/") ? "DD/MM/YYYY" : "YYYY-MM-DD",
				);
				if (birthday.isValid()) {
					item.Birthday = birthday.format("YYYY-MM-DD");
				} else {
					delete item.Birthday;
				}
			}

			if (!_.isEmpty(item["Visible in App"]) && _.isEmpty(item.VisibleInApp)) {
				item.VisibleInApp = item["Visible in App"];
				delete item["Visible in App"];
			}

			if (_.isUndefined(item.VisibleInApp)) {
				delete item.VisibleInApp;
			} else {
				item.VisibleInApp = item.VisibleInApp !== "FALSE";
			}

			item.Location = this.props.auth.site;

			if (_.isEmpty(item.Tags)) {
				delete item.Tags;
			}
		});

		this.setState(
			{
				csvData: _.filter(newData, (ev) => {
					return !ev.RowIsEmpty;
				}),
			},
			this.recomputeTypes,
		);
	}

	handleFileChange(event) {
		const file = event.target.files[0];

		var self = this;

		Papa.parse(file, {
			header: true,
			keepEmptyRows: false,
			skipEmptyLines: true,
			complete: function (results, file) {
				self.setCSV(results.data);
				self.setState({ fileError: false });
			},
			error: function () {
				console.log("Parsing fail:");
				self.setState({ csvData: [], fileError: true });
			},
		});
	}

	isEmail(email) {
		const atpos = email.indexOf("@");
		const dotpos = email.lastIndexOf(".");
		if (atpos < 1 || dotpos < atpos + 2 || dotpos + 2 >= email.length) {
			return false;
		}
		return true;
	}

	renderCSVData(additionalFields) {
		if (_.isEmpty(this.state.csvData)) {
			return null;
		}
		return this.state.csvData.map((ev, index) => {
			if (ev == null) return null;
			const emailInvalid =
				requiresEmail &&
				(_.isEmpty(ev.Email) ||
					(!_.isEmpty(ev.Email) && !this.isEmail(ev.Email)));
			return (
				<tr key={index}>
					<td style={this.cellStyle(false)}>{ev.Name}</td>
					<td style={this.cellStyle(false)}>{ev.Phone}</td>
					<td style={this.cellStyle(emailInvalid)}>{ev.Email}</td>
					<td
						style={this.cellStyle(
							_.isEmpty(ev.Unit) && ev.TypeDisplay === "Resident",
						)}
					>
						{ev.Unit}
					</td>
					<td style={this.cellStyle(false)}>
						{!_.isEmpty(ev.Birthday)
							? moment(ev.Birthday, "YYYY-MM-DD").format("D MMM YYYY")
							: ""}
					</td>
					<td style={this.cellStyle(ev._isMaster || _.isEmpty(ev.Type))}>
						{ev._isMaster ? "Master not allowed" : ev.TypeDisplay}
					</td>
					<td style={this.cellStyle(false)}>{ev.Tags}</td>
					<td style={this.cellStyle(false)}>
						{ev.VisibleInApp || _.isUndefined(ev.VisibleInApp)
							? "TRUE"
							: "FALSE"}
					</td>
					{additionalFields.map((field) => {
						const fieldKeyToUse = `${field.fieldType}_${field.key}`;
						return (
							<td
								key={fieldKeyToUse}
								style={this.cellStyle(_.isEmpty(ev[fieldKeyToUse]))}
							>
								{ev[fieldKeyToUse]}
							</td>
						);
					})}
				</tr>
			);
		});
	}

	renderAdditionalFieldsHelp(additionalFields) {
		if (_.isEmpty(additionalFields)) {
			return null;
		}
		return (
			<Text type="help" className="marginTop-30">
				When importing custom fields or profile fields, make sure the header row
				contains the field key in the format of either CF_key or PF_key as
				indicated below.
			</Text>
		);
	}

	render() {
		const additionalFields = this.getAdditionalFieldDefinitions();
		const { csvData, success } = this.state;
		return (
			<OverlayPage>
				<OverlayPageContents noBottomButtons={success}>
					<OverlayPageSection className="pageSectionWrapper--newPopup">
						{success ? (
							this.renderSuccess()
						) : (
							<div>
								<div className="padding-60 paddingVertical-40 bottomDivideBorder">
									<Text type="formTitleLarge" className="marginBottom-16">
										Import users from CSV
									</Text>
									<div
										style={{
											backgroundColor: hexToRGBAstring(
												COLOUR_BRANDING_ACTION,
												0.1,
											),
											borderLeft: `3px solid ${COLOUR_BRANDING_ACTION}`,
											borderRadius: 5,
											padding: "12px 16px",
											marginBottom: 24,
										}}
									>
										<Text type="help" className="marginBottom-4">
											You are uploading users to
										</Text>
										<Text type="formTitleSmall">{this.currentSiteName()}</Text>
									</div>
									<Text type="help" className="marginBottom-24">
										Upload a CSV to create or update users in bulk. Not sure of
										the format? Download a template with the correct headers and
										a sample row.
									</Text>
									<div className="marginBottom-24">
										<Button
											inline
											buttonType="outlinedAction"
											isActive
											onClick={this.downloadTemplate}
										>
											Download CSV template
										</Button>
									</div>
									<Text type="formLabel" className="marginBottom-8">
										CSV file
									</Text>
									{this.state.uploadingFile ? (
										<Text type="body">Uploading file…</Text>
									) : (
										<FormControl
											type="file"
											accept=".csv"
											onChange={(e) => this.handleFileChange(e)}
										/>
									)}
								</div>

								{csvData.length > 0 && (
									<div className="padding-60 paddingVertical-40">
										<Text type="formTitleSmall" className="marginBottom-16">
											Preview &amp; options
										</Text>

										{this.hasMasterRows() && (
											<Text type="error" className="marginBottom-16">
												Master user types can't be imported. Remove or change
												the highlighted rows before uploading.
											</Text>
										)}

										{this.renderTypeControls()}

										<div className="marginTop-16 marginBottom-8">
											<CheckBox
												id="sendWelcome"
												label="Send welcome email/text for new users"
												isActive={this.state.sendWelcome}
												onChange={() =>
													this.setState({
														sendWelcome: !this.state.sendWelcome,
													})
												}
											/>
										</div>

										{this.renderAdditionalFieldsHelp(additionalFields)}

										<Table
											className="plussBorderedTable plussBorderedTable--notEmpty"
											style={{ minWidth: "100%", marginTop: 24 }}
										>
											<thead>
												<tr>
													<th style={{ minWidth: 100 }}>Name</th>
													<th style={{ minWidth: 100 }}>Phone Number</th>
													<th style={{ minWidth: 100 }}>Email</th>
													<th style={{ minWidth: 100 }}>Address</th>
													<th style={{ minWidth: 100 }}>Birthday</th>
													<th style={{ minWidth: 100 }}>User Type</th>
													<th style={{ minWidth: 100 }}>Tags</th>
													<th style={{ minWidth: 100 }}>Visible in App</th>
													{additionalFields.map((field) => {
														return (
															<th style={{ minWidth: 100 }} key={field.key}>
																{field.fieldType}_{field.key}
																<br />
																<br />
																{field.label}
															</th>
														);
													})}
												</tr>
											</thead>
											<tbody>{this.renderCSVData(additionalFields)}</tbody>
										</Table>

										{this.renderError()}
									</div>
								)}
							</div>
						)}
					</OverlayPageSection>
				</OverlayPageContents>
				<OverlayPageBottomButtons>
					{this.renderSubmit()}
				</OverlayPageBottomButtons>
			</OverlayPage>
		);
	}
}

const mapStateToProps = (state) => {
	const { auth } = state;
	return { auth };
};

export default connect(mapStateToProps, { usersUpdate })(
	withRouter(BulkImport),
);
